/**
 * DebugUnlockDialog — 디버그 메뉴 unlock 토큰 입력.
 *
 * 진입: 내 정보의 "앱 버전" 라벨 10-tap (3초 윈도우).
 * 동작:
 *   1) 토큰 입력 → BE `/api/v1/user/debug/verify-token` 호출
 *   2) valid=true → useDebugStore.unlock() + onUnlock() 콜백 호출
 *   3) 3회 실패 → 60초 lockout (클라이언트 측)
 *
 * 보안: 입력값은 type="password" 로 마스킹. 메모리 외에는 어디에도 저장하지 않음.
 *
 * 서버 전환 패널(히든):
 *   토큰 검증은 "현재 연결된 서버"로 호출되므로, 그 서버가 죽어 있으면 검증이 안 돼
 *   디버그 메뉴에 못 들어가고 → API 주소도 못 바꾸는 순환에 갇힌다. 이를 풀기 위해
 *   다이얼로그 제목("🔧 디버그 모드")을 3초 내 10번 탭하면 서버 전환 패널이 노출된다
 *   (디버그 모달 자체와 동일한 10-tap 게이트 → 이중 게이트). 살아있는 서버로 바꾼 뒤
 *   토큰을 검증하면 된다. 변경은 setApiBaseOverride(localStorage)로 즉시 반영되며
 *   axios request 인터셉터가 매 요청 시 getEffectiveApiBase()를 라이브로 읽으므로
 *   리로드 없이 다음 verifyToken 부터 새 서버로 향한다.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDebugStore } from '../../store/debugStore';
import { debugApi } from '../../api/debug';
import { ApiError } from '../../lib/errors';
import {
  API_BASE_PRESETS,
  isApiOverrideAllowed,
  getEffectiveApiBase,
  setApiBaseOverride,
} from '../../config/env';
import styles from './DebugUnlockDialog.module.css';

const MAX_FAILED_ATTEMPTS = 3;
const LOCKOUT_MS = 60_000;

/** 서버 전환 패널 노출 게이트 — 제목 10-tap (내 정보와 동일 정책). */
const TAP_THRESHOLD = 10;
const TAP_WINDOW_MS = 3000;

interface Props {
  open: boolean;
  onClose: () => void;
  onUnlock?: () => void;
}

export default function DebugUnlockDialog({ open, onClose, onUnlock }: Props) {
  const unlock = useDebugStore((s) => s.unlock);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);

  // === 서버 전환 패널 (히든 — 제목 10-tap 으로 노출) ===
  const [serverPanelOpen, setServerPanelOpen] = useState(false);
  const [serverInput, setServerInput] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverBase, setServerBase] = useState(() => getEffectiveApiBase());
  const tapCountRef = useRef(0);
  const firstTapAtRef = useRef(0);

  // 다이얼로그 열릴 때 input focus + 상태 초기화
  useEffect(() => {
    if (open) {
      setToken('');
      setError(null);
      // failedCount / lockedUntil 은 다이얼로그 재오픈 시 유지 (lockout 우회 방지)
      // 서버 패널은 재오픈 시 다시 숨김 + 탭 카운터 리셋.
      setServerPanelOpen(false);
      setServerError(null);
      setServerInput('');
      setServerBase(getEffectiveApiBase());
      tapCountRef.current = 0;
      firstTapAtRef.current = 0;
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // ESC 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // lockout 카운터 — 1초 tick
  useEffect(() => {
    if (!lockedUntil) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockedUntil]);

  if (!open) return null;

  const remainingLockSec =
    lockedUntil && lockedUntil > now ? Math.ceil((lockedUntil - now) / 1000) : 0;
  const locked = remainingLockSec > 0;

  // 제목 10-tap → 서버 전환 패널 노출 (내 정보의 "앱 버전" 10-tap 과 동일 로직).
  const handleTitleTap = () => {
    // 운영 빌드는 서버 전환 자체가 막혀 있다(env.isApiOverrideAllowed) — 패널도 열지 않는다.
    if (serverPanelOpen || !isApiOverrideAllowed()) return;
    const tapNow = Date.now();
    if (tapCountRef.current === 0 || tapNow - firstTapAtRef.current > TAP_WINDOW_MS) {
      tapCountRef.current = 1;
      firstTapAtRef.current = tapNow;
      return;
    }
    tapCountRef.current += 1;
    if (tapCountRef.current >= TAP_THRESHOLD) {
      tapCountRef.current = 0;
      firstTapAtRef.current = 0;
      setServerPanelOpen(true);
    }
  };

  const applyServer = () => {
    const trimmed = serverInput.trim();
    if (!trimmed) {
      setServerError('URL 을 입력해주세요');
      return;
    }
    // 상대 경로(Vite proxy) 또는 절대 URL 허용 — DebugMenuPage 와 동일 검증.
    if (!trimmed.startsWith('/') && !/^https?:\/\//.test(trimmed)) {
      setServerError('형식이 올바르지 않아요 (http(s):// 또는 / 로 시작)');
      return;
    }
    setApiBaseOverride(trimmed);
    setServerBase(getEffectiveApiBase());
    setServerError(null);
    // 직전 서버로 실패했던 토큰 에러 메시지 클리어 (새 서버로 재시도 가능).
    setError(null);
  };

  const resetServer = () => {
    setApiBaseOverride(null);
    setServerBase(getEffectiveApiBase());
    setServerInput('');
    setServerError(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;
    if (!token.trim()) {
      setError('토큰을 입력해주세요');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await debugApi.verifyToken(token.trim());
      if (!res.enabled) {
        setError('이 환경은 디버그 메뉴가 비활성화되어 있어요');
        return;
      }
      if (!res.valid) {
        const next = failedCount + 1;
        setFailedCount(next);
        if (next >= MAX_FAILED_ATTEMPTS) {
          setLockedUntil(Date.now() + LOCKOUT_MS);
          setError(`3회 실패 — ${LOCKOUT_MS / 1000}초 후 다시 시도해주세요`);
        } else {
          setError(`토큰이 올바르지 않아요 (${next}/${MAX_FAILED_ATTEMPTS})`);
        }
        return;
      }
      // 성공
      unlock();
      setFailedCount(0);
      setLockedUntil(null);
      onUnlock?.();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : '검증 요청에 실패했어요. 네트워크를 확인해주세요';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className={styles.backdrop}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="debug-unlock-title"
      >
        <h2
          id="debug-unlock-title"
          className={styles.title}
          onClick={handleTitleTap}
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          🔧 디버그 모드
        </h2>
        <p className={styles.description}>
          관리자에게 발급받은 토큰을 입력해주세요.
        </p>

        {serverPanelOpen && (
          <div className={styles.serverPanel}>
            <div className={styles.serverHead}>
              <span className={styles.serverLabel}>검증 서버 (현재 effective)</span>
              <code className={styles.effective}>{serverBase}</code>
            </div>
            <div className={styles.presetRow}>
              {API_BASE_PRESETS.map((p) => (
                <button
                  key={p.url}
                  type="button"
                  className={styles.preset}
                  onClick={() => {
                    setServerInput(p.url);
                    setServerError(null);
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              className={styles.input}
              value={serverInput}
              onChange={(e) => setServerInput(e.target.value)}
              placeholder="http(s)://... 또는 /api/v1"
              autoComplete="off"
              spellCheck={false}
            />
            {serverError && <span className={styles.error}>{serverError}</span>}
            <div className={styles.actions}>
              <button type="button" className={styles.cancel} onClick={resetServer}>
                초기화
              </button>
              <button type="button" className={styles.confirm} onClick={applyServer}>
                서버 변경
              </button>
            </div>
            <span className={styles.serverHint}>
              * 죽은 서버 대신 살아있는 서버로 바꿔 토큰을 검증할 수 있어요.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            ref={inputRef}
            type="password"
            className={styles.input}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="토큰 입력"
            autoComplete="off"
            spellCheck={false}
            disabled={submitting || locked}
            maxLength={128}
          />
          {error && <span className={styles.error}>{error}</span>}
          {locked && (
            <span className={styles.lockoutHint}>
              {remainingLockSec}초 후 재시도 가능
            </span>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancel}
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="submit"
              className={styles.confirm}
              disabled={submitting || locked || token.trim().length === 0}
            >
              {submitting ? '확인 중…' : '확인'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
